import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { motion } from "framer-motion";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { CurrencyGbp, TrendUp, TrendDown, Buildings, Users, WarningOctagon, Clock, Sparkle, ArrowRight, XCircle, Percent, ArrowsClockwise, ArrowsLeftRight, Receipt, Coins, PaperPlaneTilt, ArrowSquareOut, CheckCircle, Warning, RocketLaunch, CircleNotch } from "@phosphor-icons/react";

const gbp = (n) => `£${Number(n || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;

function Metric({ icon: Icon, label, full, value, sub, color, delay = 0, building, soon, valueColor, onClick }) {
  const muted = building || soon;
  const clickable = !!onClick && !muted;
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay }}
      onClick={clickable ? onClick : undefined}
      className={`card-surface p-4 ${soon ? "opacity-60" : ""} ${clickable ? "cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all" : ""}`}
      data-testid={`metric-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-$/, "")}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${color}1a` }}>
          <Icon size={14} weight="fill" style={{ color }} />
        </span>
        <span className="leading-tight min-w-0">
          <span className="overline text-navy/65 block truncate">{label}</span>
          {full && <span className="text-[0.6rem] text-navy/65 block normal-case tracking-normal leading-tight">{full}</span>}
        </span>
      </div>
      <div className={`font-heading font-black text-2xl tracking-tight ${muted ? "text-navy/25" : "text-navy"}`}
        style={!muted && valueColor ? { color: valueColor } : undefined}>
        {muted ? "—" : value}
      </div>
      <div className="text-[0.7rem] text-navy/65 mt-0.5">{soon ? "once Adfin is live" : building ? "building history…" : sub}{clickable && Number(value) !== 0 ? " · view" : ""}</div>
    </motion.div>
  );
}

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return "—"; }
};

function HealthRow({ a, kind, onClose }) {
  const [sending, setSending] = useState(false);
  const [remindedAt, setRemindedAt] = useState(a.last_reminder_at);
  const sendReminder = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/admin/billing-health/${a.id}/remind`);
      if (data.sent) {
        toast.success(`Reminder sent to ${data.email}`);
        setRemindedAt(data.last_reminder_at || new Date().toISOString());
      } else toast.message("Reminder could not be delivered");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send reminder");
    } finally {
      setSending(false);
    }
  };
  return (
    <div data-testid={`billing-health-row-${a.id}`}
      className="flex items-center justify-between gap-4 px-6 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors">
      <Link to="/admin/accounts" onClick={onClose} className="min-w-0 flex-1">
        <div className="font-semibold text-navy text-sm truncate hover:underline">{a.name}</div>
        <div className="text-xs text-navy/70 truncate">{a.owner_email} · {a.plan_name}</div>
        <div className="text-[0.7rem] mt-0.5" data-testid={`health-last-nudged-${a.id}`}>
          {remindedAt
            ? <span className="text-navy/65">Last nudged {fmtDate(remindedAt)}</span>
            : <span className="text-navy/30">Not nudged yet</span>}
        </div>
      </Link>
      <div className="text-right shrink-0">
        <div className="font-heading font-bold text-navy text-sm">{gbp(a.mrr)}<span className="text-xs font-normal text-navy/65">/mo</span></div>
        <div className="text-[0.7rem] text-navy/65">
          {kind === "cancelling" ? `ends ${fmtDate(a.current_period_end)}` : `grace to ${fmtDate(a.grace_until)}`}
        </div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <button onClick={sendReminder} disabled={sending} title="Send reminder email"
          data-testid={`health-remind-${a.id}`}
          className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg text-white transition-opacity hover:opacity-90 disabled:opacity-50"
          style={{ background: "#F37A24" }}>
          <PaperPlaneTilt size={13} weight="fill" /> {sending ? "…" : remindedAt ? "Re-send" : "Remind"}
        </button>
        {a.adfin_customer_url && (
          <a href={a.adfin_customer_url} target="_blank" rel="noopener noreferrer" title="Open in Adfin"
            data-testid={`health-adfin-${a.id}`}
            className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-navy/20 text-navy hover:bg-navy hover:text-white transition-colors">
            <ArrowSquareOut size={13} weight="bold" /> Adfin
          </a>
        )}
        {!a.adfin_customer_url && a.stripe_customer_url && (
          <a href={a.stripe_customer_url} target="_blank" rel="noopener noreferrer" title="Open in Stripe"
            data-testid={`health-stripe-${a.id}`}
            className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-navy/20 text-navy hover:bg-navy hover:text-white transition-colors">
            <ArrowSquareOut size={13} weight="bold" /> Stripe
          </a>
        )}
      </div>
    </div>
  );
}

function BillingHealthModal({ kind, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!kind) return;
    setData(null);
    api.get(`/admin/billing-health/${kind}`).then(({ data }) => setData(data)).catch(() => setData({ accounts: [] }));
  }, [kind]);
  if (!kind) return null;
  const titles = { past_due: "Past-due accounts", at_risk: "MRR at risk (past due)", cancelling: "Cancellations scheduled" };
  const rows = data?.accounts || [];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="billing-health-modal">
      <div className="absolute inset-0 bg-navy/40 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
        className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h3 className="font-heading font-bold text-navy text-lg">{titles[kind] || "Accounts"}</h3>
            <p className="text-xs text-navy/65">{data ? `${rows.length} account${rows.length === 1 ? "" : "s"}` : "Loading…"}</p>
          </div>
          <button onClick={onClose} data-testid="billing-health-close" className="text-navy/65 hover:text-navy"><XCircle size={24} weight="fill" /></button>
        </div>
        <div className="overflow-y-auto">
          {data && rows.length === 0 && (
            <div className="p-10 text-center text-navy/65 text-sm" data-testid="billing-health-empty">Nothing here right now — all healthy. 🎉</div>
          )}
          {rows.map((a) => (
            <HealthRow key={a.id} a={a} kind={kind} onClose={onClose} />
          ))}
        </div>
      </motion.div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-6">
      <h2 className="overline text-navy/65 mb-2.5">{title}</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{children}</div>
    </div>
  );
}

function GoLiveChecklist() {
  const [r, setR] = useState(null);
  const [provisioning, setProvisioning] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [togglingBilling, setTogglingBilling] = useState(false);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/admin/stripe-readiness").then(({ data }) => setR(data)).catch(() => setR(null));
  useEffect(() => { load(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (r && !r.ready) setOpen(true); }, [r?.ready]);

  const provision = async () => {
    setProvisioning(true);
    try {
      const { data } = await api.post("/admin/stripe-provision");
      setR(data.readiness);
      if (data.ok) toast.success("Stripe catalogue, webhook and portal provisioned");
      else toast.error(`Some steps failed: ${data.errors.join("; ")}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not provision Stripe");
    } finally {
      setProvisioning(false);
    }
  };

  const sendGoLive = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post("/admin/stripe-go-live-email");
      if (data.sent) toast.success(`Go-live confirmation sent to ${data.email}`);
      else toast.message("Email could not be delivered");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send confirmation");
    } finally {
      setEmailing(false);
    }
  };

  const toggleBilling = async () => {
    setTogglingBilling(true);
    try {
      const { data } = await api.post("/admin/billing-toggle", { enabled: !r.billing_enabled });
      setR(data.readiness);
      toast.success(data.enabled ? "Billing switched on — you can charge customers 🎉" : "Billing switched off");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change billing");
    } finally {
      setTogglingBilling(false);
    }
  };

  if (!r) return null;
  const pending = r.total - r.passed;
  const otherBlockers = r.checks.filter((c) => !c.ok && c.label !== "Billing enabled").length;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
      className="card-surface p-5 mb-6" data-testid="go-live-checklist">
      <div className="flex items-center justify-between gap-3 cursor-pointer" onClick={() => setOpen((o) => !o)} data-testid="go-live-toggle">
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: r.ready ? "#00A85915" : "#F37A2415" }}>
            <RocketLaunch size={18} weight="fill" style={{ color: r.ready ? "#00A859" : "#F37A24" }} />
          </span>
          <div className="min-w-0">
            <h2 className="font-heading font-bold text-navy text-lg leading-tight">Stripe go-live checklist</h2>
            <p className="text-xs text-navy/65">
              {r.ready
                ? "All checks passed — you're ready to charge customers."
                : `${r.passed}/${r.total} checks passed · ${pending} to sort before launch`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${r.test_mode ? "bg-[#F37A24]/15 text-[#B4560F]" : (r.live_mode ? "bg-[#00A859]/15 text-[#00753E]" : "bg-slate-100 text-navy/70")}`}
            data-testid="go-live-mode-badge">
            {r.test_mode ? "TEST MODE" : (r.live_mode ? "LIVE MODE" : "NO KEY")}
          </span>
          <ArrowRight size={16} weight="bold" className={`text-navy/40 transition-transform ${open ? "rotate-90" : ""}`} />
        </div>
      </div>

      {r.test_mode && (
        <div className="mt-3 flex items-start gap-2 rounded-xl bg-[#F37A24]/10 border border-[#F37A24]/25 px-3.5 py-2.5" data-testid="go-live-test-banner">
          <Warning size={16} weight="fill" className="text-[#B4560F] mt-0.5 shrink-0" />
          <p className="text-xs text-[#8a4410] leading-relaxed">
            You're on a <strong>test</strong> Stripe key — no real payments will be taken. Swap <code className="font-mono">STRIPE_API_KEY</code> for your live <code className="font-mono">sk_live_…</code> key before launching.
          </p>
        </div>
      )}

      {open && (
        <div className="mt-4 space-y-2" data-testid="go-live-checks">
          {r.checks.map((c, i) => (
            <div key={i} className="flex items-start gap-3 py-2 border-b border-slate-50 last:border-0" data-testid={`go-live-check-${i}`}>
              {c.ok
                ? <CheckCircle size={18} weight="fill" className={c.warn ? "text-amber-500 shrink-0 mt-0.5" : "text-positive shrink-0 mt-0.5"} style={{ color: c.warn ? undefined : "#00A859" }} />
                : <Warning size={18} weight="fill" className="text-[#E5484D] shrink-0 mt-0.5" />}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-navy">{c.label}</div>
                <div className="text-xs text-navy/70">{c.detail}</div>
                {!c.ok && c.hint && <div className="text-[0.7rem] text-navy/50 mt-0.5">{c.hint}</div>}
              </div>
            </div>
          ))}
          <div className="pt-2">
            <button onClick={provision} disabled={provisioning} data-testid="go-live-provision-btn"
              className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg text-white transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{ background: "#0E0F3F" }}>
              {provisioning ? <CircleNotch size={15} weight="bold" className="animate-spin" /> : <ArrowsClockwise size={15} weight="bold" />}
              {provisioning ? "Provisioning…" : "Provision Stripe now"}
            </button>
            {r.ready && (
              <button onClick={sendGoLive} disabled={emailing} data-testid="go-live-email-btn"
                className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg text-white transition-opacity hover:opacity-90 disabled:opacity-50 ml-2"
                style={{ background: "#00A859" }}>
                {emailing ? <CircleNotch size={15} weight="bold" className="animate-spin" /> : <RocketLaunch size={15} weight="fill" />}
                {emailing ? "Sending…" : "Send go-live confirmation"}
              </button>
            )}
            <span className="text-[0.7rem] text-navy/50 ml-3">
              {r.ready ? "Everything's ready — email yourself the launch confirmation." : "Creates products, prices, webhook & portal (safe to re-run)."}
            </span>
          </div>

          <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between gap-3" data-testid="billing-toggle-row">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-navy">Billing switch</div>
              <div className="text-xs text-navy/60">
                {r.billing_enabled
                  ? "Billing is ON — customers can be charged."
                  : otherBlockers > 0
                    ? `Resolve ${otherBlockers} check${otherBlockers === 1 ? "" : "s"} above, then flip this on.`
                    : "Everything else is ready — flip the switch to go live."}
              </div>
            </div>
            <button onClick={toggleBilling} disabled={togglingBilling || (!r.billing_enabled && otherBlockers > 0)}
              data-testid="billing-toggle-btn" role="switch" aria-checked={r.billing_enabled}
              className={`relative w-14 h-7 rounded-full transition-colors shrink-0 disabled:opacity-40 disabled:cursor-not-allowed ${r.billing_enabled ? "bg-[#00A859]" : "bg-slate-300"}`}>
              <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${r.billing_enabled ? "translate-x-7" : ""}`} />
            </button>
          </div>
        </div>
      )}
    </motion.div>
  );
}

function DunningInsights() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dunning-insights").then(({ data }) => setD(data)).catch(() => setD(null)); }, []);
  if (!d) return null;
  return (
    <div className="mb-6" data-testid="dunning-insights">
      <h2 className="overline text-navy/65 mb-2.5">Dunning recovery</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Metric icon={Coins} label="Recovered (30d)" full="Won back by dunning" value={gbp(d.recovered_30d)} sub={`${d.count_30d} payment${d.count_30d === 1 ? "" : "s"} recovered`} color="#00A859" delay={0} />
        <Metric icon={Coins} label="Recovered (90d)" full="Won back by dunning" value={gbp(d.recovered_90d)} sub={`${d.count_90d} in last 90 days`} color="#00A859" delay={0.03} />
        <Metric icon={Receipt} label="Recovered all-time" value={gbp(d.recovered_all)} sub={`${d.count_all} total recoveries`} color="#0E0F3F" delay={0.06} />
        <Metric icon={PaperPlaneTilt} label="Dunning emails" full="Accounts nudged" value={d.emails_sent || 0} sub={`${d.currently_past_due} still past due`} color="#F37A24" delay={0.09} />
      </div>
      {(d.recent || []).length > 0 && (
        <div className="card-surface mt-3 overflow-hidden" data-testid="dunning-recent">
          <div className="px-5 py-3 border-b border-slate-100 text-sm font-semibold text-navy">Recent recoveries</div>
          <div className="divide-y divide-slate-50">
            {d.recent.map((e, i) => (
              <div key={i} className="flex items-center justify-between px-5 py-2.5" data-testid={`dunning-event-${i}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-navy truncate">{e.account_name || e.billing_email || "Account"}</div>
                  <div className="text-[0.7rem] text-navy/55">{fmtDate(e.at)}</div>
                </div>
                <span className="font-heading font-bold text-positive text-sm" style={{ color: "#00A859" }}>+{gbp(e.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const [s, setS] = useState(null);
  const [trend, setTrend] = useState(null);
  const [drill, setDrill] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setS(data)).catch(() => {});
    api.get("/admin/stats/trend?days=90").then(({ data }) => setTrend(data.snapshots || [])).catch(() => setTrend([]));
  }, []);

  const c = s?.counts || {};
  const chartData = (trend || []).map((r) => ({
    date: r.date?.slice(5),
    mrr: r.mrr,
  }));

  return (
    <AdminLayout>
      <div className="mb-6">
        <div className="overline text-navy/65 mb-1">Platform</div>
        <h1 className="font-heading font-black text-navy text-3xl tracking-tight">Revenue dashboard</h1>
        <p className="text-navy/70 text-sm mt-1">The health of your whole book at a glance.</p>
      </div>

      {!s ? (
        <div className="py-20 text-center text-navy/65 font-mono text-sm tracking-widest">LOADING…</div>
      ) : (
        <>
          <GoLiveChecklist />

          <div className="mb-6">
            <h2 className="overline text-navy/65 mb-2.5">Revenue</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div className="grid grid-cols-2 gap-3">
                <Metric icon={CurrencyGbp} label="MRR" full="Monthly Recurring Revenue" value={gbp(s.mrr)} sub={`from ${c.active || 0} active`} color="#00A859" delay={0} />
                <Metric icon={TrendUp} label="ARR" full="Annual Recurring Revenue" value={gbp(s.arr)} sub="annualised" color="#0E0F3F" delay={0.03} />
                <Metric icon={CurrencyGbp} label="ARPA" full="Avg Revenue Per Account" value={gbp(s.arpa)} sub="avg / active account" color="#F37A24" delay={0.06} />
                <Metric icon={Coins} label="LTV" full="Lifetime Value" value={gbp(s.ltv)} sub="ARPA ÷ churn" color="#0E0F3F" delay={0.09} building={s.ltv == null} />
              </div>
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: 0.06 }}
                className="card-surface p-5 flex flex-col" data-testid="mrr-trend">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-heading font-bold text-navy text-lg">MRR trend</h3>
                  <span className="text-sm font-semibold text-positive">{gbp(s.mrr)}/mo now</span>
                </div>
                <p className="text-xs text-navy/65 mb-3">Recorded daily — history builds over time.</p>
                {chartData.length >= 2 ? (
                  <div className="flex-1 min-h-[180px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="mrrFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#00A859" stopOpacity={0.35} />
                            <stop offset="100%" stopColor="#00A859" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eef0f5" vertical={false} />
                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#8a8a99" }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: "#8a8a99" }} axisLine={false} tickLine={false} width={48}
                          tickFormatter={(v) => `£${v}`} />
                        <Tooltip formatter={(v) => [gbp(v), "MRR"]} contentStyle={{ borderRadius: 10, border: "1px solid #eef0f5", fontSize: 12 }} />
                        <Area type="monotone" dataKey="mrr" stroke="#00A859" strokeWidth={2.5} fill="url(#mrrFill)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center text-center py-8" data-testid="mrr-trend-empty">
                    <TrendUp size={28} className="mb-2 text-navy/25" />
                    <p className="text-sm text-navy/70">Your MRR trend chart will appear here as daily snapshots accumulate (from today).</p>
                  </div>
                )}
              </motion.div>
            </div>
          </div>

          <Section title="Growth & acquisition">
            <Metric icon={Clock} label="New (30d)" full="New signups" value={s.signups_30d || 0} sub="in last 30 days" color="#F37A24" delay={0.12} />
            <Metric icon={TrendUp} label="New MRR (30d)" full="Won in 30 days" value={gbp(s.new_mrr_30d)} sub="from new accounts" color="#00A859" delay={0.15} />
            <Metric icon={Sparkle} label="Trialing" value={c.trialing || 0} sub={`${s.trials_ending_7d || 0} ending ≤7 days`} color="#F37A24" delay={0.18} />
            <Metric icon={Percent} label="Trial → Paid" full="Conversion rate" value={`${s.trial_conversion ?? 0}%`} sub="of trials converted" color="#F37A24" delay={0.21} building={s.trial_conversion == null} />
            <Metric icon={ArrowsLeftRight} label="Expansion MRR" full="Upsell / add-ons" value={gbp(s.expansion_mrr)} sub={s.expansion_mrr == null ? "" : `over ${s.retention_period_days ?? 30}d`} color="#0E0F3F" delay={0.24} building={s.expansion_mrr == null} />
          </Section>

          <Section title="Retention & churn">
            <Metric icon={Users} label="Active" value={c.active || 0} sub={`of ${c.total || 0} accounts`} color="#00A859" delay={0.27} />
            <Metric icon={TrendDown} label="Churn rate" full="Accounts, 30 days" value={`${s.churn_rate ?? 0}%`} sub={`${s.churned_count_30d || 0} cancelled`} color="#E5484D" delay={0.3} />
            <Metric icon={TrendDown} label="Churned MRR (30d)" full="Lost in 30 days" value={gbp(s.churned_mrr_30d)} sub="from cancellations" color="#E5484D" delay={0.33} />
            <Metric icon={ArrowsLeftRight} label="Net New MRR" full="New − churned (30d)" value={gbp(s.net_new_mrr_30d)} sub="net movement" color="#0E0F3F" delay={0.36} valueColor={(s.net_new_mrr_30d || 0) >= 0 ? "#00A859" : "#E5484D"} />
            <Metric icon={Percent} label="NRR" full="Net Revenue Retention" value={`${s.nrr ?? 0}%`} sub={s.nrr == null ? "" : `${s.gross_retention ?? 0}% gross`} color="#0E0F3F" delay={0.39} building={s.nrr == null} />
            <Metric icon={XCircle} label="Cancelled" value={c.cancelled || 0} sub="churned accounts" color="#64748B" delay={0.42} />
          </Section>

          <Section title="Book & engagement">
            <Metric icon={Buildings} label="Companies" full="Under management" value={s.companies} sub="entities linked" color="#F37A24" delay={0.45} />
            <Metric icon={Buildings} label="Avg entities" full="Per account" value={s.avg_entities ?? 0} sub="entities / account" color="#F37A24" delay={0.48} />
            <Metric icon={ArrowsClockwise} label="Active syncing" full="Synced in 30 days" value={s.active_syncing_30d || 0} sub="accounts syncing" color="#00A859" delay={0.51} />
            <Metric icon={WarningOctagon} label="Suspended" value={c.suspended || 0} sub="non-payment" color="#E5484D" delay={0.54} />
          </Section>

          <Section title="Billing health · Stripe">
            <Metric icon={WarningOctagon} label="Past due" full="Failed payment" value={s.past_due_count || 0} sub="in grace period" color="#E5484D" delay={0.57} onClick={() => setDrill("past_due")} />
            <Metric icon={CurrencyGbp} label="£ at risk" full="MRR in grace" value={gbp(s.at_risk_mrr)} sub="if not recovered" color="#F37A24" delay={0.6} onClick={() => setDrill("at_risk")} />
            <Metric icon={XCircle} label="Cancelling" full="Ends at period end" value={s.pending_cancellations || 0} sub="scheduled to cancel" color="#64748B" delay={0.63} onClick={() => setDrill("cancelling")} />
          </Section>

          <DunningInsights />

          <div className="mb-6">
            <div className="flex items-center justify-between mb-2.5">
              <h2 className="overline text-navy/65">Plan distribution</h2>
              <Link to="/admin/accounts" data-testid="view-accounts-link" className="text-sm font-semibold text-[#F37A24] hover:underline inline-flex items-center gap-1">
                View accounts <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="plan-distribution">
              {(s.by_plan || []).map((p, i) => (
                <motion.div key={p.plan_id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: 0.66 + i * 0.03 }}
                  className="card-surface p-4" data-testid={`plan-card-${p.plan_id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#0E0F3F1a" }}>
                      <Buildings size={14} weight="fill" style={{ color: "#0E0F3F" }} />
                    </span>
                    <span className="overline text-navy/65 truncate">{p.name}</span>
                  </div>
                  <div className="font-heading font-black text-2xl text-navy tracking-tight">{p.companies}</div>
                  <div className="text-[0.7rem] text-navy/65 mt-0.5">
                    {p.companies === 1 ? "company" : "companies"} · {p.count} account{p.count === 1 ? "" : "s"} · {gbp(p.mrr)}/mo
                  </div>
                </motion.div>
              ))}
              {(s.by_plan || []).length === 0 && <div className="col-span-full card-surface p-6 text-center text-navy/65">No accounts yet.</div>}
            </div>
          </div>
        </>
      )}
      <BillingHealthModal kind={drill} onClose={() => setDrill(null)} />
    </AdminLayout>
  );
}
